import { AbstractControl } from '@angular/forms';
import { Input } from '@angular/core';

import { ThfBreadcrumb } from '@totvs/thf-ui';

import { ThfJobSchedulerInternal } from './interfaces/thf-job-scheduler-internal.interface';
import { ThfPageJobSchedulerInternal } from './thf-page-job-scheduler-internal';
import { ThfPageJobSchedulerService } from './thf-page-job-scheduler.service';

/**
 * @description
 *
 * O `thf-page-job-scheduler` Ã© uma pÃ¡gina para criaÃ§Ã£o e atualizaÃ§Ã£o do *Job Scheduler* de forma simplificada, apenas informando
 * o serviÃ§o (endpoint) para consumo, sem a necessidade de criar componentes e tratamentos dos dados.
 */
export class ThfPageJobSchedulerBaseComponent {

  /** Objeto com propriedades do breadcrumb. */
  @Input('t-breadcrumb') breadcrumb?: ThfBreadcrumb = { items: [] };

  /**
   * Endpoint usado pelo template para requisiÃ§Ã£o do recurso que serÃ¡ utilizado para criaÃ§Ã£o e ediÃ§Ã£o.
   *
   * #### Processos
   *
   * Em sua inicializaÃ§Ã£o serÃ¡ verificado a existÃªncia do endpoint `{end-point}/processes`.
   * Caso o endpoint seja vÃ¡lido, serÃ¡ utilizado um componente para selecionar o processo e filtrÃ¡-los.
   * Caso nÃ£o seja vÃ¡lido, serÃ¡ utilizado um campo de entrada de texto para informar o endpoint.
   *
   * Para realizar o filtro de busca do processo, serÃ¡ enviado o conteÃºdo a ser filtrado atravÃ©s da propriedade *search*.
   * Os processos devem retornar uma lista de objetos que seguem a definiÃ§Ã£o de dados abaixo:
   *
   * ```
   * GET {end-point}/processes?search=ac04
   * ...
   * { "processID": "string", "description": "string" }
   * ```
   *
   * Para retornar parÃ¢metros dos processos, serÃ¡ realizado um `GET` como exemplificado abaixo.
   * Os parÃ¢metros devem retornar uma lista de objetos que seguem a interface [ThfDynamicFormField](/documentation/thf-dynamic-form).
   *
   * ```
   * GET {end-point}/processes/:id/parameters
   * ...
   * { items: [{ "property": "server" }, { "property": "program" }] }
   * ```
   *
   * #### Salvar e Atualizar
   *
   * Para salvar o recurso, serÃ¡ feito uma requisiÃ§Ã£o de criaÃ§Ã£o no mesmo endpoint, passando os valores
   * preenchidos pelo usuÃ¡rio via *payload*. Abaixo uma requisiÃ§Ã£o `POST` disparada,
   * onde as propriedades do *Job Scheduler* foram preenchidas:
   *
   * ```
   *  POST /api/thf-samples/v1/jobschedulers HTTP/1.1
   *  Host: localhost:4000
   *  Connection: keep-alive
   *  Accept: application/json, text/plain
   *  ...
   * ```
   *
   * *Request payload*:
   *
   * ```
   * { "firstExecution": "2018-12-07T00:00:01-00:00", "recurrent": true, "daily": { "hour": 10, "minute": 12 }, "processID": "ac0405" }
   * ```
   *
   * Caso queira que o template carregue um recurso jÃ¡ existente, deve ser incluÃ­do um parÃ¢metro na rota chamado `id`.
   *
   * Exemplo de configuraÃ§Ã£o de rota:
   *
   * ```
   *  RouterModule.forRoot([
   *    ...
   *    { path: 'edit/:id', component: ExampleJobSchedulerComponent },
   *    ...
   *  ],
   * ```
   *
   * Baseado nisso, na inicializaÃ§Ã£o do template serÃ¡ disparado uma requisiÃ§Ã£o para buscar o recurso que serÃ¡ editado.
   *
   * ```
   * GET {end-point}/{id}
   * ```
   *
   * Ao salvar o recurso serÃ¡ disparado um `PUT` com os dados preenchidos. Abaixo uma requisiÃ§Ã£o `PUT` disparada,
   * onde a propriedade *recurrent* e *daily* foram preenchidas/atualizadas e o `id` da url Ã© 1:
   *
   * ```
   *  PUT /api/thf-samples/v1/jobschedulers/1 HTTP/1.1
   *  Host: localhost:4000
   *  Connection: keep-alive
   *  Accept: application/json, text/plain
   *  ...
   * ```
   *
   * *Request payload*:
   *
   * ```
   * { "firstExecution": "2018-12-07T00:00:01-00:00", "recurrent": false, "daily": { "hour": 11, "minute": 30 }, "processID": "ac0405" }
   * ```
   */
  @Input('t-service-api') serviceApi: string;

  /** TÃ­tulo da pÃ¡gina. */
  @Input('t-title') title: string;

  model: ThfJobSchedulerInternal = new ThfPageJobSchedulerInternal();

  constructor(protected thfPageJobSchedulerService: ThfPageJobSchedulerService) {}

  protected loadData(id: string | number) {
    if (!id) {
      this.model = new ThfPageJobSchedulerInternal();
      return;
    }

    this.thfPageJobSchedulerService.getResource(id).toPromise().then((response: ThfJobSchedulerInternal) => {
      this.model = response;
    }).catch(() => {
      this.model = new ThfPageJobSchedulerInternal();
    });

  }

  protected markAsDirtyInvalidControls(controls: { [key: string]: AbstractControl } ) {
    for (const key in controls) {
      if (controls.hasOwnProperty(key)) {
        const control = controls[key];

        if (control.invalid) {
          control.markAsDirty();
        }
      }
    }
  }

}
